import { ComponentProps, useLayoutEffect } from 'react'
import { AnimatedScalingIndicator } from '@scene'
import { Vector3 } from 'three'
import { v4 } from 'uuid'
import { ThreeEvent } from '@react-three/fiber'
import { SceneElement, useControlStore } from '@editorStores'

/**
 * Adds a temporary point marker overlay to the scene.
 * Useful for drawing attention to a single position without changing the model itself.
 */
const useSceneMarker = ({
  marker,
  enabled,
  color,
  size,
  disableDepthTest = false,
  onClick,
  animated = true,
}: {
  marker: { x: number; y: number; z: number }
  enabled: boolean
  color?: string
  size?: number
  disableDepthTest?: boolean
  animated?: boolean
  onClick?: (event: ThreeEvent<MouseEvent>) => void
}) => {
  const addSceneMarker = useControlStore(state => state.addSceneElement)
  const removeSceneMarker = useControlStore(state => state.removeSceneElement)

  useLayoutEffect(() => {
    if (enabled) {
      const id = v4()

      const properties: ComponentProps<typeof AnimatedScalingIndicator> = {
        size,
        onClick: (event: ThreeEvent<MouseEvent>) => {
          onClick?.(event as ThreeEvent<MouseEvent>)
          event.stopPropagation()
        },
        position: new Vector3(marker.x, marker.y, marker.z),
        color: color || 'hotpink',
        opacity: 0.99,
        renderOrder: 999,
        animated,
        materialProps: { depthTest: !disableDepthTest },
      }
      const sceneMarker: SceneElement<ComponentProps<typeof AnimatedScalingIndicator>> = {
        id,
        properties,
        component: AnimatedScalingIndicator,
      }
      addSceneMarker(sceneMarker)

      return () => removeSceneMarker(id)
    }
  }, [
    addSceneMarker,
    color,
    enabled,
    marker,
    removeSceneMarker,
    disableDepthTest,
    onClick,
    size,
    animated,
  ])
}

export default useSceneMarker
